import { captureException } from "@sentry/react";
import {
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
} from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { IdleSoftRefresh } from "./components/IdleSoftRefresh";
import { ImpersonationBanner } from "./components/ImpersonationBanner";
import { RouterErrorComponent } from "./components/RouterErrorComponent";
import { RouterPageTitle } from "./components/RouterPageTitle";
import { Toaster } from "./components/ui/sonner";
import { Providers } from "./context/providers";

// Layout components
import KitLayout from "./layouts/KitLayout";
import SidebarLayout from "./layouts/SidebarLayout";

// Pages
import { PageViewTracker } from "./components/PageViewTracker";
import { GlobalAdminGroupLayout } from "./layouts/GlobalAdminGroupLayout";
import { GlobalAdminLayout } from "./layouts/GlobalAdminLayout";
import { GlobalAdminOrganizationLayout } from "./layouts/GlobalAdminOrganizationLayout";
import { GroupLayout } from "./layouts/GroupLayout";
import { AuthenticationGuard } from "./layouts/Guards/AuthenticationGuard";
import MembershipRoleGuard from "./layouts/Guards/MembershipRoleGuard";
import RoleGuard from "./layouts/Guards/RoleGuard";
import UserSettingsLayout from "./layouts/UserSettingsLayout";
import { createPageRoute } from "./lib/router";
import ConnectorsPage from "./pages/ConnectorsPage";
import { ConversationsPage } from "./pages/ConversationsPage";
import { GroupHomePage } from "./pages/GroupHomePage";
import { InterviewsPage } from "./pages/InterviewsPage";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { ProjectsPage } from "./pages/ProjectsPage";
import { UserGroupsPage } from "./pages/UserGroupsPage";
import UserSettingsPage from "./pages/UserSettingsPage";
import { GlobalAdminGroupCustomPromptsPage } from "./pages/global-admin/GlobalAdminGroupCustomPromptsPage";
import GlobalAdminGroupDetailsPage from "./pages/global-admin/GlobalAdminGroupDetailsPage";
import { GlobalAdminGroupExpertsPage } from "./pages/global-admin/GlobalAdminGroupExpertsPage";
import GlobalAdminGroupFeatureFlagsPage from "./pages/global-admin/GlobalAdminGroupFeatureFlagsPage";
import { GlobalAdminGroupPresetsPage } from "./pages/global-admin/GlobalAdminGroupPresetsPage";
import { GlobalAdminGroupTemplatesPage } from "./pages/global-admin/GlobalAdminGroupTemplatesPage";
import GlobalAdminGroupUsersPage from "./pages/global-admin/GlobalAdminGroupUsersPage";
import GlobalAdminGroupsPage from "./pages/global-admin/GlobalAdminGroupsPage";
import { GlobalAdminOrganizationCustomPromptsPage } from "./pages/global-admin/GlobalAdminOrganizationCustomPromptsPage";
import GlobalAdminOrganizationDetailsPage from "./pages/global-admin/GlobalAdminOrganizationDetailsPage";
import GlobalAdminOrganizationFeatureFlagsPage from "./pages/global-admin/GlobalAdminOrganizationFeatureFlagsPage";
import GlobalAdminOrganizationMembersPage from "./pages/global-admin/GlobalAdminOrganizationMembersPage";
import GlobalAdminOrganizationsPage from "./pages/global-admin/GlobalAdminOrganizationsPage";
import GlobalAdminTasksPage from "./pages/global-admin/GlobalAdminTasksPage";
import GlobalAdminUserPage from "./pages/global-admin/GlobalAdminUserPage";
import GlobalAdminUsersPage from "./pages/global-admin/GlobalAdminUsersPage";
import CollectionsPage from "./pages/group-admin/CollectionsPage";
import ExpertsPage from "./pages/group-admin/ExpertsPage";
import GroupAdminRedirectPage from "./pages/group-admin/GroupAdminRedirectPage";
import { InterviewAdminPage } from "./pages/group-admin/InterviewAdminPage";
import { InterviewsAdminPage } from "./pages/group-admin/InterviewsAdminPage";
import PresetsPage from "./pages/group-admin/PresetsPage";
import { RepositoriesPage } from "./pages/group-admin/RepositoriesPage";
import ScriptCreatePage from "./pages/group-admin/ScriptCreatePage";
import ScriptEditPage from "./pages/group-admin/ScriptEditPage";
import { SharedFoldersPage } from "./pages/group-admin/SharedFoldersPage";
import AdminSkillsPage from "./pages/group-admin/SkillsPage";
import TemplatesPage from "./pages/group-admin/TemplatesPage";
import UsagePage from "./pages/group-admin/UsagePage";
import UsersPage from "./pages/group-admin/UsersPage";
import ArtifactsPage from "./pages/kit/ArtifactsPage";
import CollectionSettingsPage from "./pages/kit/CollectionSettingsPage";
import { ConversationPage } from "./pages/kit/ConversationPage";
import { ConversationSettingsPage } from "./pages/kit/ConversationSettingsPage";
import { DeletePage } from "./pages/kit/DeletePage";
import DocumentPage from "./pages/kit/DocumentPage";
import { ExpertContextPage } from "./pages/kit/ExpertContextPage";
import { ExpertFoldersPage } from "./pages/kit/ExpertFoldersPage";
import ExpertSettingsPage from "./pages/kit/ExpertSettingsPage";
import { ExpertToneOfVoicePage } from "./pages/kit/ExpertToneOfVoicePage";
import { FileDebugPage } from "./pages/kit/FileDebugPage";
import { FolderPage } from "./pages/kit/FolderPage";
import { FoldersPage } from "./pages/kit/FoldersPage";
import InterviewSettingsPage from "./pages/kit/InterviewSettingsPage";
import { KitDebugPage } from "./pages/kit/KitDebugPage";
import KitRedirectPage from "./pages/kit/KitRedirectPage";
import PerspectivePage from "./pages/kit/PerspectivePage";
import PresetSettingsPage from "./pages/kit/PresetSettingsPage";
import ProjectSettingsPage from "./pages/kit/ProjectSettingsPage";
import SkillCreatePage from "./pages/kit/SkillCreatePage";
import { SkillPage } from "./pages/kit/SkillPage";
import SkillSettingsPage from "./pages/kit/SkillSettingsPage";
import TemplatePage from "./pages/kit/TemplatePage";
import TemplateSettingsPage from "./pages/kit/TemplateSettingsPage";

// ============================================================
// Root Route
// ============================================================

const rootRoute = createRootRoute({
  staticData: {
    title: "Dash",
    eventName: "dash_page_viewed",
  },
  component: () => (
    <>
      <Helmet>
        <meta
          name="description"
          content="AI agents. Trained on your knowledge. Ready to coach your human and AI teams."
        />
      </Helmet>

      <Providers>
        <RouterPageTitle />
        <PageViewTracker />
        <IdleSoftRefresh />
        <ImpersonationBanner />
        <AuthenticationGuard />
      </Providers>

      <Toaster />
    </>
  ),
});

// ============================================================
// Top-level Routes
// ============================================================

const indexRoute = createPageRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: LandingPage,
  staticData: {
    title: "Landing",
    eventName: "landing_page_viewed",
  },
});

const loginRoute = createPageRoute({
  getParentRoute: () => rootRoute,
  path: "u/login",
  component: LoginPage,
  staticData: {
    title: "Login",
    eventName: "login_page_viewed",
  },
});

const userWorkspacesRoute = createPageRoute({
  getParentRoute: () => rootRoute,
  path: "u/workspaces",
  component: UserGroupsPage,
  staticData: {
    title: "Workspaces",
    eventName: "workspaces_page_viewed",
  },
});

// ============================================================
// User Settings Routes
// ============================================================

const userSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "u/settings",
});

const userSettingsLayoutRoute = createRoute({
  getParentRoute: () => userSettingsRoute,
  id: "user-settings-layout",
  component: UserSettingsLayout,
});

const userSettingsIndexRoute = createPageRoute({
  getParentRoute: () => userSettingsLayoutRoute,
  path: "/",
  component: UserSettingsPage,
  staticData: {
    title: "User Settings",
    eventName: "user_settings_page_viewed",
  },
});

// ============================================================
// Group Routes
// ============================================================

const groupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "w/$groupSlug",
  component: GroupLayout,
});

// -- Group Sidebar Layout (pathless) --

const groupSidebarLayout = createRoute({
  getParentRoute: () => groupRoute,
  id: "group-sidebar",
  component: SidebarLayout,
});

const groupHomeRoute = createPageRoute({
  getParentRoute: () => groupSidebarLayout,
  path: "/",
  component: GroupHomePage,
  staticData: {
    title: "Home",
    eventName: "home_page_viewed",
  },
});

const projectsRoute = createPageRoute({
  getParentRoute: () => groupSidebarLayout,
  path: "workbooks",
  component: ProjectsPage,
  staticData: {
    title: "Workbooks",
    eventName: "projects_page_viewed",
  },
});

const projectsRedirectRoute = createRoute({
  getParentRoute: () => groupSidebarLayout,
  path: "projects",
  beforeLoad: ({ params }) => {
    throw redirect({
      to: "/w/$groupSlug/workbooks",
      params: {
        groupSlug: (params as Record<string, string>).groupSlug,
      },
      replace: true,
    });
  },
});

const chatsRoute = createPageRoute({
  getParentRoute: () => groupSidebarLayout,
  path: "chats",
  component: ConversationsPage,
  staticData: {
    title: "Chats",
    eventName: "chats_page_viewed",
  },
});

const interviewsRoute = createPageRoute({
  getParentRoute: () => groupSidebarLayout,
  path: "interviews",
  component: InterviewsPage,
  staticData: {
    title: "Interviews",
    eventName: "interviews_page_viewed",
  },
});

const connectorsRoute = createPageRoute({
  getParentRoute: () => groupSidebarLayout,
  path: "connectors",
  component: ConnectorsPage,
  staticData: {
    title: "Connectors",
    eventName: "connectors_page_viewed",
  },
});

const conversationsRedirectRoute = createRoute({
  getParentRoute: () => groupSidebarLayout,
  path: "conversations",
  beforeLoad: ({ params }) => {
    throw redirect({
      to: "/w/$groupSlug/chats",
      params: {
        groupSlug: (params as Record<string, string>).groupSlug,
      },
    });
  },
});

// ============================================================
// Group Admin/Settings Routes
// ============================================================

const settingsRoute = createRoute({
  getParentRoute: () => groupRoute,
  path: "settings",
  component: () => <RoleGuard groupRoles={["creator", "admin"]} />,
});

const settingsLayoutRoute = createRoute({
  getParentRoute: () => settingsRoute,
  id: "settings-sidebar",
  component: SidebarLayout,
});

const settingsIndexRoute = createPageRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: "/",
  component: GroupAdminRedirectPage,
  staticData: {
    title: "Group Settings",
    eventName: "group_settings_page_viewed",
  },
});

const settingsRepositoriesRoute = createPageRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: "shared-group-knowledge",
  component: RepositoriesPage,
  staticData: {
    title: "Shared Group Knowledge",
    eventName: "shared_group_knowledge_page_viewed",
  },
});

const settingsSharedFoldersRoute = createPageRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: "shared-knowledge",
  component: SharedFoldersPage,
  staticData: {
    title: "Shared Knowledge",
    eventName: "shared_knowledge_page_viewed",
  },
});

const settingsTemplatesRoute = createPageRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: "templates",
  component: TemplatesPage,
  staticData: {
    title: "Templates",
    eventName: "templates_page_viewed",
  },
});

const settingsExpertsRoute = createPageRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: "experts",
  component: ExpertsPage,
  staticData: {
    title: "Experts",
    eventName: "experts_page_viewed",
  },
});

const settingsSkillsRoute = createPageRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: "skills",
  component: AdminSkillsPage,
  staticData: {
    title: "Skills",
    eventName: "skills_page_viewed",
  },
});

const settingsPresetsRoute = createPageRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: "presets",
  component: PresetsPage,
  staticData: {
    title: "Presets",
    eventName: "presets_page_viewed",
  },
});

const settingsCollectionsRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: "collections",
  component: CollectionsPage,
});

const settingsInterviewsRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: "interviews",
});

const settingsInterviewsIndexRoute = createPageRoute({
  getParentRoute: () => settingsInterviewsRoute,
  path: "/",
  component: InterviewsAdminPage,
  staticData: {
    title: "Interview Admin",
    eventName: "interview_admin_page_viewed",
  },
});

const settingsInterviewDetailRoute = createPageRoute({
  getParentRoute: () => settingsInterviewsRoute,
  path: "$kitUuid",
  component: InterviewAdminPage,
  staticData: {
    title: "Interview",
    eventName: "interview_page_viewed",
  },
});

const settingsScriptNewRoute = createPageRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: "script/new",
  component: ScriptCreatePage,
  staticData: {
    title: "New Script",
    eventName: "new_script_page_viewed",
  },
});

const settingsScriptEditRoute = createPageRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: "script/$scriptUuid",
  component: ScriptEditPage,
  staticData: {
    title: "Edit Script",
    eventName: "edit_script_page_viewed",
  },
});

const settingsKnowledgeRoute = createPageRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: "knowledge",
  component: RepositoriesPage,
  staticData: {
    title: "Knowledge",
    eventName: "knowledge_page_viewed",
  },
});

// ============================================================
// Group Admin Settings Routes
// ============================================================

const groupSettingsLayout = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  id: "group-settings-guard",
  component: () => <RoleGuard groupRoles={["admin"]} />,
});

const membershipSettingsLayout = createRoute({
  getParentRoute: () => groupRoute,
  path: "settings/users",
  component: MembershipRoleGuard,
});

const membershipSettingsSidebar = createRoute({
  getParentRoute: () => membershipSettingsLayout,
  id: "membership-settings-sidebar",
  component: SidebarLayout,
});

const settingsUsersRoute = createPageRoute({
  getParentRoute: () => membershipSettingsSidebar,
  path: "/",
  component: UsersPage,
  staticData: {
    title: "Users",
    eventName: "users_page_viewed",
  },
});

const settingsUsageRoute = createPageRoute({
  getParentRoute: () => groupSettingsLayout,
  path: "/usage",
  component: UsagePage,
  staticData: {
    title: "Usage",
    eventName: "usage_page_viewed",
  },
});

// ============================================================
// Kit Routes
// ============================================================

const kitRoute = createRoute({
  getParentRoute: () => groupRoute,
  path: "k/$kitUuid",
  component: KitLayout,
});

const kitIndexRoute = createRoute({
  getParentRoute: () => kitRoute,
  path: "/",
  component: KitRedirectPage,
});

const kitChatRoute = createRoute({
  getParentRoute: () => kitRoute,
  path: "chat",
});

const kitChatIndexRoute = createRoute({
  getParentRoute: () => kitChatRoute,
  path: "/",
  component: ConversationPage,
});

const kitChatDetailRoute = createPageRoute({
  getParentRoute: () => kitChatRoute,
  path: "$chatUuid",
  component: ConversationPage,
  staticData: {
    title: "Conversation",
    eventName: "conversation_page_viewed",
  },
});

const kitDocumentRoute = createPageRoute({
  getParentRoute: () => kitRoute,
  path: "document/$documentUuid",
  component: DocumentPage,
  staticData: {
    title: "Document",
    eventName: "document_page_viewed",
  },
});

const kitExpertKnowledgeRoute = createPageRoute({
  getParentRoute: () => kitRoute,
  path: "expert-knowledge",
  component: ExpertFoldersPage,
  staticData: {
    title: "Expert Knowledge",
    eventName: "expert_knowledge_page_viewed",
  },
});

const kitKnowledgeRoute = createRoute({
  getParentRoute: () => kitRoute,
  path: "knowledge",
});

const kitKnowledgeIndexRoute = createPageRoute({
  getParentRoute: () => kitKnowledgeRoute,
  path: "/",
  component: FoldersPage,
  staticData: {
    title: "Knowledge",
    eventName: "knowledge_page_viewed",
  },
});

const kitFolderRoute = createRoute({
  getParentRoute: () => kitKnowledgeRoute,
  path: "$folderUuid",
});

const kitFolderIndexRoute = createPageRoute({
  getParentRoute: () => kitFolderRoute,
  path: "/",
  component: FolderPage,
  staticData: {
    title: "Folder",
    eventName: "folder_page_viewed",
  },
});

const kitFolderSplatRoute = createPageRoute({
  getParentRoute: () => kitFolderRoute,
  path: "$",
  component: FolderPage,
  staticData: {
    title: "Folder",
    eventName: "folder_page_viewed",
  },
});

const kitTemplateRoute = createPageRoute({
  getParentRoute: () => kitRoute,
  path: "template",
  component: TemplatePage,
  staticData: {
    title: "Template",
    eventName: "template_page_viewed",
  },
});

const kitExpertRoute = createRoute({
  getParentRoute: () => kitRoute,
  path: "expert",
});

const kitExpertIndexRoute = createRoute({
  getParentRoute: () => kitExpertRoute,
  path: "/",
  beforeLoad: ({ params }) => {
    throw redirect({
      to: "/w/$groupSlug/k/$kitUuid/expert/context",
      params: {
        groupSlug: (params as Record<string, string>).groupSlug,
        kitUuid: (params as Record<string, string>).kitUuid,
      },
    });
  },
});

const kitExpertContextRoute = createPageRoute({
  getParentRoute: () => kitExpertRoute,
  path: "context",
  component: ExpertContextPage,
  staticData: {
    title: "Expert Context",
    eventName: "expert_context_page_viewed",
  },
});

const kitExpertToneRoute = createPageRoute({
  getParentRoute: () => kitExpertRoute,
  path: "tone-of-voice",
  component: ExpertToneOfVoicePage,
  staticData: {
    title: "Tone of Voice",
    eventName: "tone_of_voice_page_viewed",
  },
});

const kitPerspectiveRoute = createPageRoute({
  getParentRoute: () => kitRoute,
  path: "perspectives/$perspectiveUuid",
  component: PerspectivePage,
  staticData: {
    title: "Perspective",
    eventName: "perspective_page_viewed",
  },
});

const kitSkillDetailRoute = createPageRoute({
  getParentRoute: () => kitRoute,
  path: "skills/$skillUuid",
  component: SkillPage,
  staticData: {
    title: "Skill",
    eventName: "skill_page_viewed",
  },
});

const kitSkillCreateRoute = createPageRoute({
  getParentRoute: () => kitRoute,
  path: "skill",
  component: SkillCreatePage,
  staticData: {
    title: "Create Skill",
    eventName: "create_skill_page_viewed",
  },
});

const kitExpertSettingsRoute = createPageRoute({
  getParentRoute: () => kitRoute,
  path: "expert-settings",
  component: ExpertSettingsPage,
  staticData: {
    title: "Expert Settings",
    eventName: "expert_settings_page_viewed",
  },
});

const kitTemplateSettingsRoute = createPageRoute({
  getParentRoute: () => kitRoute,
  path: "template-settings",
  component: TemplateSettingsPage,
  staticData: {
    title: "Template Settings",
    eventName: "template_settings_page_viewed",
  },
});

const kitSkillSettingsRoute = createPageRoute({
  getParentRoute: () => kitRoute,
  path: "skill-settings",
  component: SkillSettingsPage,
  staticData: {
    title: "Skill Settings",
    eventName: "skill_settings_page_viewed",
  },
});

const kitProjectSettingsRoute = createPageRoute({
  getParentRoute: () => kitRoute,
  path: "workbook-settings",
  component: ProjectSettingsPage,
  staticData: {
    title: "Workbook Settings",
    eventName: "project_settings_page_viewed",
  },
});

const kitProjectSettingsRedirectRoute = createRoute({
  getParentRoute: () => kitRoute,
  path: "project-settings",
  beforeLoad: ({ params }) => {
    throw redirect({
      to: "/w/$groupSlug/k/$kitUuid/workbook-settings",
      params: {
        groupSlug: (params as Record<string, string>).groupSlug,
        kitUuid: (params as Record<string, string>).kitUuid,
      },
      replace: true,
    });
  },
});

const kitChatSettingsRoute = createPageRoute({
  getParentRoute: () => kitRoute,
  path: "chat-settings",
  component: ConversationSettingsPage,
  staticData: {
    title: "Chat Settings",
    eventName: "chat_settings_page_viewed",
  },
});

const kitInterviewSettingsRoute = createPageRoute({
  getParentRoute: () => kitRoute,
  path: "interview-settings",
  component: InterviewSettingsPage,
  staticData: {
    title: "Interview Settings",
    eventName: "interview_settings_page_viewed",
  },
});

const kitPresetSettingsRoute = createPageRoute({
  getParentRoute: () => kitRoute,
  path: "preset-settings",
  component: PresetSettingsPage,
  staticData: {
    title: "Preset Settings",
    eventName: "preset_settings_page_viewed",
  },
});

const kitCollectionSettingsRoute = createPageRoute({
  getParentRoute: () => kitRoute,
  path: "collection-settings",
  component: CollectionSettingsPage,
  staticData: {
    title: "Collections Settings",
    eventName: "collections_settings_page_viewed",
  },
});

const kitArtifactsRoute = createPageRoute({
  getParentRoute: () => kitRoute,
  path: "artifacts",
  component: ArtifactsPage,
  staticData: {
    title: "Artifacts",
    eventName: "artifacts_page_viewed",
  },
});

const kitFileDebugRoute = createRoute({
  getParentRoute: () => kitRoute,
  path: "folders/$folderUuid/files/$fileUuid/debug",
  component: () => <RoleGuard systemRoles={["super"]} />,
});

const kitFileDebugIndexRoute = createPageRoute({
  getParentRoute: () => kitFileDebugRoute,
  path: "/",
  component: FileDebugPage,
  staticData: {
    title: "File Debug",
    eventName: "file_debug_page_viewed",
  },
});

const kitDeleteRoute = createPageRoute({
  getParentRoute: () => kitRoute,
  path: "delete",
  component: DeletePage,
  staticData: {
    title: "Delete Kit",
    eventName: "delete_kit_page_viewed",
  },
});

const kitDebugRoute = createPageRoute({
  getParentRoute: () => kitRoute,
  path: "debug",
  component: KitDebugPage,
  staticData: {
    title: "Kit Debug",
    eventName: "kit_debug_page_viewed",
  },
});

// Catch-all inside group
const groupNotFoundRoute = createPageRoute({
  getParentRoute: () => groupRoute,
  path: "$",
  component: NotFoundPage,
  staticData: {
    title: "Not Found",
    eventName: "not_found_page_viewed",
  },
});

// ============================================================
// Global Admin Routes
// ============================================================

const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "admin",
  component: () => <RoleGuard systemRoles={["super"]} />,
});

const adminLayoutRoute = createRoute({
  getParentRoute: () => adminRoute,
  id: "admin-layout",
  component: GlobalAdminLayout,
});

const adminIndexRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/admin/groups" });
  },
});

const adminUsersRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "users",
});

const adminUsersIndexRoute = createPageRoute({
  getParentRoute: () => adminUsersRoute,
  path: "/",
  component: GlobalAdminUsersPage,
  staticData: {
    title: "Admin Users",
    eventName: "admin_users_page_viewed",
  },
});

const adminUserDetailRoute = createPageRoute({
  getParentRoute: () => adminUsersRoute,
  path: "$userUuid",
  component: GlobalAdminUserPage,
  staticData: {
    title: "Admin User",
    eventName: "admin_user_page_viewed",
  },
});

const adminOrganizationsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "organizations",
});

const adminOrganizationsIndexRoute = createPageRoute({
  getParentRoute: () => adminOrganizationsRoute,
  path: "/",
  component: GlobalAdminOrganizationsPage,
  staticData: {
    title: "Admin Organizations",
    eventName: "admin_organizations_page_viewed",
  },
});

const adminOrganizationDetailRoute = createRoute({
  getParentRoute: () => adminOrganizationsRoute,
  path: "$organizationUuid",
});

const adminOrganizationLayoutRoute = createRoute({
  getParentRoute: () => adminOrganizationDetailRoute,
  id: "admin-organization-layout",
  component: GlobalAdminOrganizationLayout,
});

const adminOrganizationIndexRoute = createRoute({
  getParentRoute: () => adminOrganizationLayoutRoute,
  path: "/",
  beforeLoad: ({ params }) => {
    throw redirect({
      to: "/admin/organizations/$organizationUuid/details",
      params: {
        organizationUuid: (params as { organizationUuid: string })
          .organizationUuid,
      },
    });
  },
});

const adminOrganizationDetailsRoute = createPageRoute({
  getParentRoute: () => adminOrganizationLayoutRoute,
  path: "details",
  component: GlobalAdminOrganizationDetailsPage,
  staticData: {
    title: "Organization Details",
    eventName: "organization_details_page_viewed",
  },
});

const adminOrganizationFeatureFlagsRoute = createPageRoute({
  getParentRoute: () => adminOrganizationLayoutRoute,
  path: "feature-flags",
  component: GlobalAdminOrganizationFeatureFlagsPage,
  staticData: {
    title: "Organization Feature Flags",
    eventName: "organization_feature_flags_page_viewed",
  },
});

const adminOrganizationMembersRoute = createPageRoute({
  getParentRoute: () => adminOrganizationLayoutRoute,
  path: "members",
  component: GlobalAdminOrganizationMembersPage,
  staticData: {
    title: "Organization Members",
    eventName: "organization_members_page_viewed",
  },
});

const adminOrganizationCustomPromptsRoute = createPageRoute({
  getParentRoute: () => adminOrganizationLayoutRoute,
  path: "custom-prompts",
  component: GlobalAdminOrganizationCustomPromptsPage,
  staticData: {
    title: "Organization Custom Prompts",
    eventName: "organization_custom_prompts_page_viewed",
  },
});

const adminTasksRoute = createPageRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "tasks",
  component: GlobalAdminTasksPage,
  staticData: {
    title: "Admin Tasks",
    eventName: "admin_tasks_page_viewed",
  },
});

const adminGroupsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "groups",
});

const adminGroupsIndexRoute = createPageRoute({
  getParentRoute: () => adminGroupsRoute,
  path: "/",
  component: GlobalAdminGroupsPage,
  staticData: {
    title: "Admin Groups",
    eventName: "admin_groups_page_viewed",
  },
});

const adminGroupDetailRoute = createRoute({
  getParentRoute: () => adminGroupsRoute,
  path: "$groupUuid",
});

const adminGroupLayoutRoute = createRoute({
  getParentRoute: () => adminGroupDetailRoute,
  id: "admin-group-layout",
  component: GlobalAdminGroupLayout,
});

const adminGroupIndexRoute = createRoute({
  getParentRoute: () => adminGroupLayoutRoute,
  path: "/",
  beforeLoad: ({ params }) => {
    throw redirect({
      to: "/admin/groups/$groupUuid/details",
      params: {
        groupUuid: (params as { groupUuid: string }).groupUuid,
      },
    });
  },
});

const adminGroupDetailsRoute = createPageRoute({
  getParentRoute: () => adminGroupLayoutRoute,
  path: "details",
  component: GlobalAdminGroupDetailsPage,
  staticData: {
    title: "Group Details",
    eventName: "group_details_page_viewed",
  },
});

const adminGroupFeatureFlagsRoute = createPageRoute({
  getParentRoute: () => adminGroupLayoutRoute,
  path: "feature-flags",
  component: GlobalAdminGroupFeatureFlagsPage,
  staticData: {
    title: "Feature Flags",
    eventName: "feature_flags_page_viewed",
  },
});

const adminGroupCustomPromptsRoute = createPageRoute({
  getParentRoute: () => adminGroupLayoutRoute,
  path: "custom-prompts",
  component: GlobalAdminGroupCustomPromptsPage,
  staticData: {
    title: "Custom Prompts",
    eventName: "group_custom_prompts_page_viewed",
  },
});

const adminGroupUsersRoute = createPageRoute({
  getParentRoute: () => adminGroupLayoutRoute,
  path: "users",
  component: GlobalAdminGroupUsersPage,
  staticData: {
    title: "Group Users",
    eventName: "group_users_page_viewed",
  },
});

const adminGroupExpertsRoute = createPageRoute({
  getParentRoute: () => adminGroupLayoutRoute,
  path: "experts",
  component: GlobalAdminGroupExpertsPage,
  staticData: {
    title: "Group Experts",
    eventName: "group_experts_page_viewed",
  },
});

const adminGroupPresetsRoute = createPageRoute({
  getParentRoute: () => adminGroupLayoutRoute,
  path: "presets",
  component: GlobalAdminGroupPresetsPage,
  staticData: {
    title: "Group Presets",
    eventName: "group_presets_page_viewed",
  },
});

const adminGroupTemplatesRoute = createPageRoute({
  getParentRoute: () => adminGroupLayoutRoute,
  path: "templates",
  component: GlobalAdminGroupTemplatesPage,
  staticData: {
    title: "Group Templates",
    eventName: "group_templates_page_viewed",
  },
});

// ============================================================
// Global Catch-all
// ============================================================

const notFoundRoute = createPageRoute({
  getParentRoute: () => rootRoute,
  path: "$",
  component: NotFoundPage,
  staticData: {
    title: "Not Found",
    eventName: "not_found_page_viewed",
  },
});

// ============================================================
// Route Tree
// ============================================================

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  userWorkspacesRoute,

  // User settings
  userSettingsRoute.addChildren([
    userSettingsLayoutRoute.addChildren([userSettingsIndexRoute]),
  ]),

  // Group
  groupRoute.addChildren([
    // Sidebar pages
    groupSidebarLayout.addChildren([
      groupHomeRoute,
      projectsRoute,
      projectsRedirectRoute,
      chatsRoute,
      interviewsRoute,
      connectorsRoute,
      conversationsRedirectRoute,
    ]),

    membershipSettingsLayout.addChildren([
      membershipSettingsSidebar.addChildren([settingsUsersRoute]),
    ]),

    // Group admin settings
    settingsRoute.addChildren([
      settingsLayoutRoute.addChildren([
        settingsIndexRoute,
        settingsRepositoriesRoute,
        settingsSharedFoldersRoute,
        groupSettingsLayout.addChildren([settingsUsageRoute]),
        settingsTemplatesRoute,
        settingsExpertsRoute,
        settingsSkillsRoute,
        settingsPresetsRoute,
        settingsCollectionsRoute,
        settingsInterviewsRoute.addChildren([
          settingsInterviewsIndexRoute,
          settingsInterviewDetailRoute,
        ]),
        settingsScriptNewRoute,
        settingsScriptEditRoute,
        settingsKnowledgeRoute,
      ]),
    ]),

    // Kit
    kitRoute.addChildren([
      kitIndexRoute,
      kitChatRoute.addChildren([kitChatIndexRoute, kitChatDetailRoute]),
      kitDocumentRoute,
      kitExpertKnowledgeRoute,
      kitKnowledgeRoute.addChildren([
        kitKnowledgeIndexRoute,
        kitFolderRoute.addChildren([kitFolderIndexRoute, kitFolderSplatRoute]),
      ]),
      kitTemplateRoute,
      kitExpertRoute.addChildren([
        kitExpertIndexRoute,
        kitExpertContextRoute,
        kitExpertToneRoute,
      ]),
      kitPerspectiveRoute,
      kitSkillDetailRoute,
      kitSkillCreateRoute,
      kitExpertSettingsRoute,
      kitTemplateSettingsRoute,
      kitSkillSettingsRoute,
      kitProjectSettingsRoute,
      kitProjectSettingsRedirectRoute,
      kitChatSettingsRoute,
      kitInterviewSettingsRoute,
      kitPresetSettingsRoute,
      kitCollectionSettingsRoute,
      kitArtifactsRoute,
      kitFileDebugRoute.addChildren([kitFileDebugIndexRoute]),
      kitDeleteRoute,
      kitDebugRoute,
    ]),

    groupNotFoundRoute,
  ]),

  // Global admin
  adminRoute.addChildren([
    adminLayoutRoute.addChildren([
      adminIndexRoute,
      adminTasksRoute,
      adminUsersRoute.addChildren([adminUsersIndexRoute, adminUserDetailRoute]),
      adminOrganizationsRoute.addChildren([
        adminOrganizationsIndexRoute,
        adminOrganizationDetailRoute.addChildren([
          adminOrganizationLayoutRoute.addChildren([
            adminOrganizationIndexRoute,
            adminOrganizationDetailsRoute,
            adminOrganizationFeatureFlagsRoute,
            adminOrganizationMembersRoute,
            adminOrganizationCustomPromptsRoute,
          ]),
        ]),
      ]),
      adminGroupsRoute.addChildren([
        adminGroupsIndexRoute,
        adminGroupDetailRoute.addChildren([
          adminGroupLayoutRoute.addChildren([
            adminGroupIndexRoute,
            adminGroupDetailsRoute,
            adminGroupFeatureFlagsRoute,
            adminGroupCustomPromptsRoute,
            adminGroupUsersRoute,
            adminGroupExpertsRoute,
            adminGroupPresetsRoute,
            adminGroupTemplatesRoute,
          ]),
        ]),
      ]),
    ]),
  ]),

  // Global catch-all
  notFoundRoute,
]);

// ============================================================
// Router Instance
// ============================================================

export const router = createRouter({
  routeTree,
  defaultErrorComponent: RouterErrorComponent,
  defaultOnCatch: (error) => {
    captureException(error);
  },
});
